// The value each visible metric holds at the cursor, drawn on the chart beside its own curve.
//
// The readout row above the chart already carries the same figures, but reading one off it means
// looking away from the point being pointed at, and on a chart of stacked bands the eye then has to
// find its way back to the right band. These labels put the number where the answer already is.
// Drawn with the chart's own renderer rather than as Highcharts data labels: they exist for one
// bucket at a time and have to be torn down and rebuilt as the cursor moves, which is not something
// a series-level data label configuration can express.

import type { Chart, Point, SVGElement } from 'highcharts';
import { withAlpha } from '@/plugins/highcharts/chartColors';
import colors from '@/plugins/tailwind';

export type CursorLabel = {
    point: Point;
    text: string;
    color: string;
    // The value to anchor the label at, when that differs from the point's own. Water is the case
    // this exists for: its actual column is drawn capped at the norm (so the norm shows behind it
    // rather than being covered), and a label placed on the capped value sits below the top of the
    // stack it is labelling whenever actual runs over norm.
    anchorValue?: number;
};

// Clearance between one label's box and the next, on top of the boxes' own heights. Two metrics
// sharing a band (weight and Daily Gain) routinely cross, and where they do their labels land on the
// same pixel.
const MIN_LABEL_GAP_PX = 4;
// A label whose box would run past the plot's right edge is drawn on the other side of its point
// instead. Wide enough for the longest of them ("Vandnorm 00,0 l/gris/dag") plus its own padding.
const FLIP_MARGIN_PX = 130;
// How far a box sits from the point it labels, horizontally.
const LABEL_OFFSET_PX = 8;

type PlacedLabel = CursorLabel & { x: number; y: number };

// `toPixels(value, false)` rather than `point.plotY`: this chart stacks its axes into bands, and a
// point's plotY is measured from its own band's top, not the plot's - so a water column's label,
// placed from plotY, lands in the weight band above it. Chart-relative pixels are also what the
// renderer wants, so the conversion has to happen anyway.
function placeLabel(label: CursorLabel): PlacedLabel | null {
    const series = label.point.series;
    const y = label.anchorValue ?? label.point.y;
    if (!series?.xAxis || !series.yAxis || y === undefined || y === null) return null;
    return {
        ...label,
        x: series.xAxis.toPixels(label.point.x, false),
        y: series.yAxis.toPixels(y, false),
    };
}

type MeasuredLabel = { label: PlacedLabel; element: SVGElement; width: number; height: number; boxTop: number };

// Nudges boxes down until none of them overlaps the one above, each starting centred on the value it
// labels. Downwards only, and from the topmost: pushing both ways from a collision moves boxes that
// were correctly placed, and the reader's association of a number with a curve is what these are for.
//
// By measured height rather than by a fixed step, because these boxes are not one size - a water
// label carries its actual, its norm and the deviation between them on three lines, and under a
// reference it carries three more. A fixed step smaller than a box's own height leaves the tall ones
// overlapping their neighbours however far they are pushed.
function spreadVertically(measured: MeasuredLabel[]): MeasuredLabel[] {
    const sorted = [...measured].sort((a, b) => a.label.y - b.label.y);
    let previousBottom = -Infinity;
    for (const item of sorted) {
        item.boxTop = Math.max(item.label.y - item.height / 2, previousBottom + MIN_LABEL_GAP_PX);
        previousBottom = item.boxTop + item.height;
    }
    return sorted;
}

/**
 * Draws one value label per entry beside its point, returning the elements so the caller can
 * destroy them on the next cursor move. Each label carries a dot in its metric's own colour, so a
 * number is tied to a curve by colour rather than by proximity alone - proximity being exactly what
 * the de-collision above has to spend when two curves cross. The dot stays on the value it marks
 * even where the box beside it has been pushed clear of a neighbour: the dot is the claim about
 * where the reading is, and only the text needs the room.
 *
 * `text: false` draws the dots alone. That is the resting state: the readout row above the chart is
 * already carrying every figure, so the boxes would be covering the newest readings to repeat what is
 * legible six pixels higher - while the dots are what tie that row's date to a place on the chart,
 * and a 7px dot on a curve hides nothing. The two were one call because they are one label; they are
 * separable here rather than at the call site because the placement above is what decides where a dot
 * goes, and only a measured box can say that.
 */
export function drawCursorLabels(chart: Chart, labels: CursorLabel[], options: { text: boolean }): SVGElement[] {
    const placed = labels.map(placeLabel).filter((label): label is PlacedLabel => label !== null);
    const rightEdge = chart.plotLeft + chart.plotWidth;
    const drawn: SVGElement[] = [];

    // Rendered before being positioned: a box's own width and height are not known until it exists,
    // and both the flip below and the spreading above need them.
    const measured = placed.map((label) => {
        const element = chart.renderer
            .label(label.text, 0, 0, undefined, undefined, undefined, false, false, 'productivity-cursor-label')
            .attr({ fill: withAlpha(colors.white, 0.92), r: 4, padding: 3, zIndex: 9 })
            .css({ fontSize: '11px', fontWeight: '600', color: label.color })
            .add();
        const box = element.getBBox();
        return { label, element, width: box.width, height: box.height, boxTop: label.y };
    });

    for (const { label, element, width, boxTop } of spreadVertically(measured)) {
        drawn.push(
            chart.renderer
                .circle(label.x, label.y, 3.5)
                .attr({ fill: label.color, zIndex: 8 })
                .add()
        );
        // Measured either way - the box has to exist to be measured, and its height is what spaces the
        // dots' own de-collision - so an unwanted one is destroyed rather than never built.
        if (options.text) {
            const flip = label.x > rightEdge - FLIP_MARGIN_PX;
            element.attr({ x: flip ? label.x - width - LABEL_OFFSET_PX : label.x + LABEL_OFFSET_PX, y: boxTop });
            drawn.push(element);
        } else {
            element.destroy();
        }
    }

    return drawn;
}
